import locationApi from '@api/locationApi';
import IconLocation from '@assets/icons/location.svg?react';
import type { Interpolation } from '@emotion/react';
import useTimebased from '@hooks/useTimebased';
import { useTranslator } from '@i18n';
import { Group, UnstyledButton, type BoxProps } from '@mantine/core';
import { openLocationSpotlight } from '@state/locationSpotlightStore';
import { IconBarrierBlock, IconChevronDown, IconWorld } from '@tabler/icons-react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { themeColor } from '@util/color';
import maintenanceHelpers from '@util/maintenanceHelpers';
import { HTTPError } from 'ky';
import { useEffect } from 'react';

export interface LocationPickerProps extends BoxProps {
  showMaintenance?: boolean;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
}

const iconCss: Interpolation = {
  "[data-mantine-color-scheme='dark'] &": {
    color: themeColor('primary'),
  },

  "[data-mantine-color-scheme='light'] &": {
    fill: themeColor('primary', 3),
  },
};

export default function LocationPicker({ showMaintenance, leftSection, rightSection, ...props }: LocationPickerProps) {
  const t = useTranslator();
  const { locationId } = useSearch({ strict: false });
  const navigate = useNavigate();
  const [location, error] = locationApi.get(locationId ?? '').useCache({ disabled: !locationId });
  const isMaintenanceActive = useTimebased((date) => maintenanceHelpers.isActive(location?.maintenance, date));

  useEffect(() => {
    if (error instanceof HTTPError && error.response?.status === 404) {
      void navigate({ to: '.', search: { locationId: '' }, params: true });
    }
  }, [error, navigate]);

  return (
    <UnstyledButton
      variant="transparent"
      {...props}
      css={{
        height: 48,
        padding: '0 var(--mantine-spacing-md)',
        backgroundColor: 'var(--mantine-color-paper-7)',
        borderRadius: 1000,
        fontSize: 'var(--mantine-font-size-sm)',

        '&:active': {
          filter: 'brightness(1.5)',
          transform: 'translateY(1px)',
        },
      }}
      onClick={openLocationSpotlight}
      data-testid="location-select"
    >
      <Group wrap="nowrap">
        {leftSection ??
          (locationId ? (
            <IconLocation size="1.5em" css={iconCss} />
          ) : (
            <IconWorld size="1.5em" css={iconCss} strokeWidth={1} />
          ))}

        <span css={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {location?.name || locationId || t('header.global')}
        </span>

        {showMaintenance && isMaintenanceActive ? (
          <IconBarrierBlock color="var(--mantine-color-red-5)" data-testid="maintenance-indicator" />
        ) : (
          rightSection
        )}

        <IconChevronDown strokeWidth={1} />
      </Group>
    </UnstyledButton>
  );
}
